import { TECHNOLOGY_MAPPING, FUNCTION_MAPPING } from './constants'
import i18next from 'i18next'
import moment from 'moment'

export const formatDate = (date: Date, options: {
  includeDay?: boolean;
  includeMonth?: boolean;
  includeYear?: boolean;
  includeTime?: boolean;
} = {}): string => {
  const {
    includeDay = true,
    includeMonth = true,
    includeYear = true,
    includeTime = true
  } = options;
  
  const dayIndex = date.getDay();
  const dayKey = `wan.days.${dayIndex}`;
  const dayName = i18next.t(dayKey);
  
  const day = date.getDate().toString().padStart(2, '0');
  const monthIndex = date.getMonth();
  const monthKey = `wan.months.${monthIndex}`;
  const month = i18next.t(monthKey);
  const year = date.getFullYear();
  
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  let result = '';
  
  if (includeDay) {
    result += `${dayName} ${day}`;
    if (includeMonth) result += ` ${month}`;
    if (includeYear) result += ` ${year}`;
  }
  
  if (includeTime) {
    if (result) result += ' - ';
    result += `${hours}:${minutes}`;
  }
  
  return result;
}

export const normalizeTimestamp = (timestamp: string | number | Date): Date => {
  return timestamp instanceof Date
    ? timestamp
    : new Date(typeof timestamp === 'number' && timestamp < 10000000000
      ? timestamp * 1000
      : timestamp);
}

export const formatTimeForDisplay = (timestamp: string | number | Date): string => {
  const date = normalizeTimestamp(timestamp);
  if (isNaN(date.getTime())) {
    return '-'
  }
  return formatDate(date, {
    includeDay: true,
    includeMonth: true,
    includeYear: true,
    includeTime: true
  });
}

export const formatSimplifiedTimeForDisplay = (timestamp: string | number | Date): string => {
  const date = normalizeTimestamp(timestamp);
  if (isNaN(date.getTime())) {
    return '-'
  }
  const dayIndex = date.getDay();
  const dayKey = `wan.days.${dayIndex}`;
  const dayName = i18next.t(dayKey);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  
  return `${dayName} ${hours}:${minutes}`;
}

const scaleBandwidthValue = (value: number): { scaledValue: number; unitIndex: number } => {
  if (value < 1000) {
    return { scaledValue: value, unitIndex: 0 };
  } else if (value < 1000000) {
    return { scaledValue: value / 1000, unitIndex: 1 };
  } else if (value < 1000000000) {
    return { scaledValue: value / 1000000, unitIndex: 2 };
  } else {
    return { scaledValue: value / 1000000000, unitIndex: 3 };
  }
}

export const formatBandwidth = (
  value: number | null | undefined,
  isAxis = false,
  isTableData = true,
  wireType?: string | null
): string => {
  if (wireType === 'FTTH') return '-';
  
  if (value === undefined || value === null) return '-';
  if (value === 0) return '0'
  
  const actualValue = isTableData ? value * 1000 : value
  const units = ['bps', 'Kbps', 'Mbps', 'Gbps']
  const { scaledValue, unitIndex } = scaleBandwidthValue(actualValue)
  
  if (isAxis) {
    return `${scaledValue.toFixed(0)}${units[unitIndex]}`
  }
  
  return `${scaledValue.toFixed(2)} ${units[unitIndex]}`
}

export const getDurationParts = (seconds: number | null | undefined): { hours: number; minutes: number } | null => {
  if (seconds === null || seconds === undefined) return null;
  
  const duration = moment.duration(seconds, 'seconds');
  return {
    hours: Math.floor(duration.asHours()),
    minutes: duration.minutes()
  };
}

export const getTechnologyLabel = (wireType: string | null | undefined): string => {
  if (!wireType) return '-'
  
  const mapping = TECHNOLOGY_MAPPING[wireType]
  if (!mapping || mapping.label === 'MASQUER') return '-'
  
  return mapping.label
}

export const getTechnologyDescription = (wireType: string | null | undefined): string => {
  if (!wireType) return ''
  
  const mapping = TECHNOLOGY_MAPPING[wireType]
  if (!mapping || !mapping.description) return ''
  
  return i18next.t(mapping.description)
}

export const getFunctionLabel = (
  useCase: string | null | undefined,
  ownerIsWifirst: boolean | null | undefined,
  wanAccessThroughIpsec: boolean | null | undefined
): string => {
  if (!useCase) return '-'
  
  const mapping = FUNCTION_MAPPING[useCase]
  if (!mapping || mapping.label === 'A MASQUER') return '-'
  
  if (useCase === 'wan_access') {
    if (ownerIsWifirst) {
      return i18next.t(mapping.wifirstDescription || mapping.label)
    } else if (wanAccessThroughIpsec) {
      return i18next.t(mapping.clientWithIpsecDescription || mapping.label)
    }
  }
  
  return i18next.t(mapping.label)
}

export const getFunctionDescription = (
  useCase: string | null | undefined,
  ownerIsWifirst: boolean | null | undefined,
  wanAccessThroughIpsec: boolean | null | undefined
): string => {
  if (!useCase) return ''
  
  const mapping = FUNCTION_MAPPING[useCase]
  if (!mapping || !mapping.description) return ''
  
  if (useCase === 'wan_access' && !ownerIsWifirst && wanAccessThroughIpsec) {
    return i18next.t(mapping.clientWithIpsecTooltip || mapping.description)
  }
  
  return i18next.t(mapping.description)
}

export const shouldHideLine = (wireType: string, useCase: string | null | undefined): boolean => {
  if (wireType === 'IPSEC' || wireType === 'openvpn') return true
  if (useCase === 'si_access') return true
  return false
}

export const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp)
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

export const createTimeRangeParams = (
  preset: string,
  customRange: { start: Date | null; end: Date | null },
  isCustom: boolean
): { preset?: string; start?: number; end?: number } => {
  if (isCustom && customRange.start && customRange.end) {
    return {
      start: Math.floor(customRange.start.getTime() / 1000),
      end: Math.floor(customRange.end.getTime() / 1000)
    }
  }
  return { preset }
}
